import { z } from "zod";
import { localeSchema } from "./common";
import { quizConsentSchema } from "./quiz-consent";

// CAR-W-6/7 — the public BMI qualification quiz schema. The quiz is a
// pre-signup funnel that captures lifestyle/motivation/expectations so
// the team can:
//   1. Tailor onboarding copy + email follow-ups (lead nurturing).
//   2. Segment cohorts for marketing attribution.
//
// NONE of these answers gate medical eligibility — that's the intake
// (anamnes) the patient fills out post-signup, evaluated server-side by
// the deterministic hard-stop rules. Keep the quiz friction-light.
//
// The schema is data-driven so the renderer in `apps/web` (CAR-75) walks
// the question list and dispatches by `kind`, same pattern as the
// intake renderer on mobile (CAR-53).

export const bmiQuizFieldKindSchema = z.enum([
  // 1-of-N radio buttons
  "single_select",
  // M-of-N checkboxes
  "multi_select",
  // 1..5 scale rendered as a row of buttons
  "scale_1_5",
  // Open free-text input (used by the `annat` follow-up)
  "short_text",
  // F-01 v2 — decimal number entry (weight/height). The wire answer stays a
  // STRING like every other kind ("87.5"); `numeric` carries the bounds.
  "numeric",
  // "Hälsoformulär v3" §4.3 — a step that TELLS the patient something.
  //
  // "Steget är inte en fråga, skapar inget svarsvärde och får inte räknas som en obesvarad obligatorisk fråga."
  //
  // All three properties are enforced in code rather than left to whoever
  // authors the template: `isInformationStep` is the single predicate, and
  // both the renderer's advance gate and the server's required-check consult
  // it. Marking such a step `optional: true` would have worked by accident
  // and broken the moment someone tidied the flag away.
  "information_step",
]);
export type BmiQuizFieldKind = z.infer<typeof bmiQuizFieldKindSchema>;

// Inline localized copy — sv default, en alongside. The schema ships in
// contracts so the web renderer + the API validator both read the same
// option set without one re-declaring it. Adding a new option in one
// place propagates everywhere.
export const localizedSchema = z.object({
  sv: z.string(),
  en: z.string(),
});
export type Localized = z.infer<typeof localizedSchema>;

export const bmiQuizOptionSchema = z.object({
  // TES-587 — an exclusive option ("none of these", "unsure") cannot be
  // combined with the others in a multi_select; see applyExclusiveOptions.
  exclusive: z.boolean().optional(),
  value: z.string(),
  label: localizedSchema,
});
export type BmiQuizOption = z.infer<typeof bmiQuizOptionSchema>;

// A follow-up question that conditionally fires when the trigger answer
// is selected. Self-referential: a follow-up is itself a quiz field so
// branches can nest (we only use one level today but the shape allows
// more).
export const bmiQuizFieldSchema: z.ZodType<BmiQuizField> = z.lazy(() =>
  z.object({
    key: z.string(),
    kind: bmiQuizFieldKindSchema,
    label: localizedSchema,
    help: localizedSchema.optional(),
    options: z.array(bmiQuizOptionSchema).optional(),
    // True if the patient must answer this field before advancing.
    required: z.boolean().default(true),
    // Sometimes a field is informational — patient can skip without an
    // answer. Used for the "How did you find us → annat" free-text
    // follow-up.
    optional: z.boolean().optional(),
    // If present: this field only renders when the parent question's
    // answer contains `triggerValue`. Used for the medication follow-up
    // (Q6) and the "annat" free-text on Q8.
    showWhen: z
      .object({
        parentKey: z.string(),
        triggerValue: z.string().optional(),
        // §4.2 — the prescription-transfer branch opens on "Ja" OR "Osäker".
        // A single triggerValue would have forced two identical information
        // steps, which then drift apart the first time the copy is edited.
        triggerValues: z.array(z.string()).nonempty().optional(),
      })
      .refine(
        (w) => w.triggerValue !== undefined || w.triggerValues !== undefined,
        {
          message: "showWhen needs triggerValue or triggerValues",
        },
      )
      .optional(),
    // TES-396 — scale_1_5 only: per-step word labels (index 0 = value "1").
    // Presentation only; the wire answer stays the numeric string. Renderers
    // without labels fall back to bare numbers + low/high anchors.
    stepLabels: z.array(localizedSchema).length(5).optional(),
    // multi_select only — the most options the patient may pick. The help
    // text used to be the only statement of the limit ("Välj upp till 3"),
    // which meant it was advice, not a rule: nothing stopped a fourth box or
    // rejected an over-long answer. Q12's canonical spec says `max 3`, so the
    // limit now lives where both the renderers and the validator can see it.
    maxSelections: z.number().int().positive().optional(),
    // numeric only — accepted range; a comma decimal is normalized to a dot
    // before validation.
    numeric: z.object({ min: z.number(), max: z.number() }).optional(),
    unit: z.string().optional(),
    // information_step only — the step's own forward label
    // ("Fortsätt till hälsodeklarationen"). An information step's CTA is part
    // of its copy, not a generic "Nästa".
    ctaLabel: localizedSchema.optional(),
    // information_step only — body paragraphs. `help` is a single muted line
    // under the heading; an information step carries real prose, and reusing
    // `help` would have rendered the whole patient copy as fine print.
    body: z.array(localizedSchema).optional(),
    // TES-1658 — the key of the template section (`sections`) the question
    // belongs to. Presentation only: a renderer shows the section's title and
    // progress; it never changes order, branching or validation.
    section: z.string().optional(),
    // TES-1658 — a short introduction shown above the question, e.g. the line
    // that introduces the two safety questions. Copy, not a question.
    lead: localizedSchema.optional(),
    // TES-1658 — the text behind a closed "Varför frågar vi?" disclosure.
    why: localizedSchema.optional(),
  }),
);

export interface BmiQuizField {
  key: string;
  kind: BmiQuizFieldKind;
  label: Localized;
  help?: Localized;
  options?: BmiQuizOption[];
  required?: boolean;
  optional?: boolean;
  showWhen?: {
    parentKey: string;
    triggerValue?: string;
    triggerValues?: string[];
  };
  // TES-396 — scale_1_5 word labels, index 0 = value "1". Presentation only.
  stepLabels?: Localized[];
  // multi_select only — the most options the patient may pick.
  maxSelections?: number;
  numeric?: { min: number; max: number };
  unit?: string;
  ctaLabel?: Localized;
  body?: Localized[];
  section?: string;
  lead?: Localized;
  why?: Localized;
}

export const bmiQuizTemplateSchema = z.object({
  key: z.literal("bmi-quiz"),
  version: z.number().int().positive(),
  // TES-1658 — the parts the quiz is presented in, in order. Absent before
  // v13. Fields name their part with `section`.
  sections: z
    .array(z.object({ key: z.string(), title: localizedSchema }))
    .optional(),
  fields: z.array(bmiQuizFieldSchema),
});
export type BmiQuizTemplate = z.infer<typeof bmiQuizTemplateSchema>;

// Submission payload — flat answer map keyed by field.key. Values are
// `string` for single_select / scale_1_5 / short_text, and `string[]`
// for multi_select. The server validates against the published template
// at the version the client rendered.
export const bmiQuizAnswerValueSchema = z.union([
  z.string(),
  z.array(z.string()),
]);
export type BmiQuizAnswerValue = z.infer<typeof bmiQuizAnswerValueSchema>;

export const bmiQuizSubmissionSchema = z.object({
  templateVersion: z.number().int().positive(),
  // sessionId matches the client's localStorage key — lets us dedupe a
  // patient who submits twice from the same browser.
  sessionId: z.string().uuid(),
  submittedInLocale: localeSchema,
  answers: z.record(bmiQuizAnswerValueSchema),
  // TES-1587 — the explicit consent she gave before the first question.
  // Required: the API refuses a submission without it (`consent_required`).
  consent: quizConsentSchema,
  // Optional marketing source (UTM / referrer); the web captures these
  // from query params + document.referrer at submit time.
  source: z
    .object({
      utmSource: z.string().optional(),
      utmMedium: z.string().optional(),
      utmCampaign: z.string().optional(),
      referrer: z.string().optional(),
    })
    .optional(),
});
export type BmiQuizSubmission = z.infer<typeof bmiQuizSubmissionSchema>;

// Lead row shape (wire form) — what the API returns to the client +
// what the admin will eventually render in a leads dashboard.
export const leadSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  templateVersion: z.number().int(),
  answers: z.record(bmiQuizAnswerValueSchema),
  submittedInLocale: localeSchema,
  source: z
    .object({
      utmSource: z.string().nullable(),
      utmMedium: z.string().nullable(),
      utmCampaign: z.string().nullable(),
      referrer: z.string().nullable(),
    })
    .nullable(),
  createdAt: z.string(),
  patientId: z.string().uuid().nullable(),
});
export type Lead = z.infer<typeof leadSchema>;

// ---------------------------------------------------------------------------
// Published v1 — the 9 questions wired per the product spec (CAR-W-7).
// Keep this co-located with the schema so the renderer + API both see
// the same definition (no separate "questions" config file).
// ---------------------------------------------------------------------------

export const bmiQuizV1: BmiQuizTemplate = {
  key: "bmi-quiz",
  version: 1,
  fields: [
    {
      key: "activityLevel",
      kind: "single_select",
      label: {
        sv: "Hur aktiv är du i vardagen?",
        en: "How active are you day-to-day?",
      },
      options: [
        {
          value: "sedentary",
          label: { sv: "Stillasittande", en: "Sedentary" },
        },
        { value: "light", label: { sv: "Lätt aktiv", en: "Lightly active" } },
        {
          value: "moderate",
          label: { sv: "Måttligt aktiv", en: "Moderately active" },
        },
        { value: "active", label: { sv: "Aktiv", en: "Active" } },
        {
          value: "very_active",
          label: { sv: "Mycket aktiv", en: "Very active" },
        },
      ],
    },
    {
      key: "whyLoseWeight",
      kind: "multi_select",
      label: {
        sv: "Varför vill du gå ner i vikt?",
        en: "Why do you want to lose weight?",
      },
      help: {
        sv: "Välj alla som passar.",
        en: "Select all that apply.",
      },
      options: [
        {
          value: "health",
          label: { sv: "Förbättra min hälsa", en: "Improve my health" },
        },
        { value: "energy", label: { sv: "Få mer energi", en: "More energy" } },
        {
          value: "fertility",
          label: {
            sv: "Fertilitet och graviditet",
            en: "Fertility and pregnancy",
          },
        },
        {
          value: "appearance",
          label: { sv: "Hur jag ser ut", en: "How I look" },
        },
        {
          value: "medical_condition",
          label: { sv: "Sjukdomsrelaterat", en: "Medical condition" },
        },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: "impactOnLife",
      kind: "multi_select",
      label: {
        sv: "Hur mycket påverkar övervikten ditt liv?",
        en: "How much does being overweight affect your life?",
      },
      help: {
        sv: "Välj alla som passar.",
        en: "Select all that apply.",
      },
      options: [
        {
          value: "daily_activities",
          label: { sv: "Vardagliga aktiviteter", en: "Daily activities" },
        },
        { value: "sleep", label: { sv: "Sömn", en: "Sleep" } },
        {
          value: "social",
          label: { sv: "Sociala situationer", en: "Social situations" },
        },
        { value: "work", label: { sv: "Arbete", en: "Work" } },
        {
          value: "self_esteem",
          label: { sv: "Självkänsla", en: "Self-esteem" },
        },
        {
          value: "physical_pain",
          label: { sv: "Fysisk smärta", en: "Physical pain" },
        },
        {
          value: "none",
          label: { sv: "Inget av ovan", en: "None of the above" },
        },
      ],
    },
    {
      key: "emotionalEating",
      kind: "scale_1_5",
      label: {
        sv: "Hur ofta använder du mat för att må bättre?",
        en: "How often do you use food to feel better?",
      },
      // TES-396 — frequency reads as words, not bare 1–5. Replaces
      // the old "1 = aldrig, 5 = ofta" help line; wire value is still "1".."5"
      // so stored answers and the profile summary are untouched.
      stepLabels: [
        { sv: "Aldrig", en: "Never" },
        { sv: "Sällan", en: "Rarely" },
        { sv: "Ibland", en: "Sometimes" },
        { sv: "Ofta", en: "Often" },
        { sv: "Varje dag", en: "Every day" },
      ],
    },
    // TES-191 (health declaration part 1) — the "biggest obstacles" / Ella-USP
    // questions. Approved Swedish copy is the base; English derived from it.
    // These feed the profile-insight taxonomy
    // ("Biologi/Stress/Beteende/Självbild") in health-profile-summary.ts.
    {
      key: "biggestObstacles",
      kind: "multi_select",
      label: {
        sv: "Vad känner du igen dig mest i?",
        en: "What do you recognise yourself in most?",
      },
      help: {
        sv: "Välj alla som stämmer.",
        en: "Select all that apply.",
      },
      options: [
        {
          value: "regain",
          label: {
            sv: "Jag går ner men går alltid upp igen",
            en: "I lose weight but always gain it back",
          },
        },
        {
          value: "lose_momentum",
          label: {
            sv: "Jag börjar starkt men tappar",
            en: "I start strong but lose momentum",
          },
        },
        {
          value: "hunger_cravings",
          label: {
            sv: "Hunger och sug tar över",
            en: "Hunger and cravings take over",
          },
        },
        {
          value: "stress_eating",
          label: {
            sv: "Stress/trötthet påverkar mitt ätande",
            en: "Stress/fatigue affects my eating",
          },
        },
        {
          value: "deprioritize_self",
          label: {
            sv: "Jag prioriterar ofta bort mig själv",
            en: "I often deprioritise myself",
          },
        },
        {
          value: "self_criticism",
          label: {
            sv: "Jag är hård mot mig själv när jag misslyckas",
            en: "I'm hard on myself when I fail",
          },
        },
      ],
    },
    {
      key: "hardestPart",
      kind: "single_select",
      label: {
        sv: "Vad har varit svårast för dig i din viktresa?",
        en: "What's been hardest in your weight journey?",
      },
      options: [
        {
          value: "hunger_cravings",
          label: {
            sv: "Att hantera hunger och sug",
            en: "Managing hunger and cravings",
          },
        },
        {
          value: "sustaining_habits",
          label: {
            sv: "Att hålla i nya vanor över tid",
            en: "Keeping new habits over time",
          },
        },
        {
          value: "time_energy",
          label: {
            sv: "Att hitta tid och energi för mig själv",
            en: "Finding time and energy for myself",
          },
        },
        {
          value: "knowing_what_works",
          label: {
            sv: "Att veta vad som faktiskt fungerar",
            en: "Knowing what actually works",
          },
        },
        {
          value: "emotions_without_food",
          label: {
            sv: "Att hantera känslor/stress utan mat",
            en: "Handling emotions/stress without food",
          },
        },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: "howLongTried",
      kind: "single_select",
      label: {
        sv: "Hur länge har du försökt gå ner i vikt?",
        en: "How long have you tried to lose weight?",
      },
      options: [
        {
          value: "less_than_6m",
          label: { sv: "Mindre än 6 månader", en: "Less than 6 months" },
        },
        { value: "6_to_12m", label: { sv: "6–12 månader", en: "6–12 months" } },
        { value: "1_to_3y", label: { sv: "1–3 år", en: "1–3 years" } },
        { value: "3_to_5y", label: { sv: "3–5 år", en: "3–5 years" } },
        {
          value: "more_than_5y",
          label: { sv: "Mer än 5 år", en: "More than 5 years" },
        },
      ],
    },
    {
      key: "methodsTried",
      kind: "multi_select",
      label: {
        sv: "Vilka metoder har du provat?",
        en: "Which methods have you tried?",
      },
      help: {
        sv: "Välj alla som passar.",
        en: "Select all that apply.",
      },
      options: [
        { value: "diet", label: { sv: "Kost", en: "Diet" } },
        { value: "exercise", label: { sv: "Träning", en: "Exercise" } },
        {
          value: "counselling",
          label: { sv: "Kostrådgivning", en: "Nutrition counselling" },
        },
        { value: "surgery", label: { sv: "Kirurgi", en: "Surgery" } },
        { value: "medication", label: { sv: "Läkemedel", en: "Medication" } },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: "knownMedications",
      kind: "multi_select",
      label: {
        sv: "Vilka av dessa läkemedel känner du till?",
        en: "Which of these medications have you heard of?",
      },
      help: {
        sv: "Välj alla du har hört talas om.",
        en: "Select all you've heard of.",
      },
      options: [
        { value: "wegovy", label: { sv: "Wegovy", en: "Wegovy" } },
        { value: "saxenda", label: { sv: "Saxenda", en: "Saxenda" } },
        { value: "mysimba", label: { sv: "Mysimba", en: "Mysimba" } },
        { value: "ozempic", label: { sv: "Ozempic", en: "Ozempic" } },
        { value: "mounjaro", label: { sv: "Mounjaro", en: "Mounjaro" } },
        { value: "unsure", label: { sv: "Vet inte", en: "Not sure" } },
      ],
      // Conditional follow-up: only render when "medication" is in the
      // methodsTried answer set.
      showWhen: { parentKey: "methodsTried", triggerValue: "medication" },
    },
    {
      key: "speedPreference",
      kind: "single_select",
      label: {
        sv: "Hur snabbt vill du gå ner i vikt?",
        en: "How quickly do you want to lose weight?",
      },
      options: [
        {
          value: "as_fast_as_possible",
          label: { sv: "Så snabbt som möjligt", en: "As fast as possible" },
        },
        {
          value: "steady",
          label: { sv: "Stadigt över tid", en: "Steady over time" },
        },
        {
          value: "slow_sustainable",
          label: {
            sv: "Hellre långsamt och hållbart",
            en: "Slow and sustainable",
          },
        },
      ],
    },
    {
      key: "howFoundUs",
      kind: "single_select",
      label: {
        sv: "Hur hittade du oss?",
        en: "How did you find us?",
      },
      options: [
        { value: "google", label: { sv: "Google", en: "Google" } },
        { value: "instagram", label: { sv: "Instagram", en: "Instagram" } },
        { value: "tiktok", label: { sv: "TikTok", en: "TikTok" } },
        { value: "friend", label: { sv: "Väninna", en: "A friend" } },
        { value: "doctor", label: { sv: "Läkare", en: "A doctor" } },
        { value: "podcast", label: { sv: "Podd", en: "Podcast" } },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: "howFoundUsOther",
      kind: "short_text",
      label: {
        sv: "Berätta gärna mer.",
        en: "Tell us more.",
      },
      required: false,
      optional: true,
      showWhen: { parentKey: "howFoundUs", triggerValue: "other" },
    },
  ],
};

// Helper: which fields are visible given the current answer set. The
// renderer + the API validator both use this so they agree on what
// "required" means in the presence of branching.
/**
 * TES-587 — enforce exclusive options in a multi_select: choosing an
 * exclusive option keeps only it; choosing any other option drops the
 * exclusive ones. Pure; `previous` is the selection before the change.
 */
export function applyExclusiveOptions(
  field: BmiQuizField,
  previous: string[],
  next: string[],
): string[] {
  const exclusive = new Set(
    (field.options ?? []).filter((o) => o.exclusive).map((o) => o.value),
  );
  if (exclusive.size === 0) return next;
  const added = next.find((v) => !previous.includes(v));
  if (added !== undefined && exclusive.has(added)) return [added];
  return next.filter((v) => !exclusive.has(v));
}

export function visibleBmiQuizFields(
  template: BmiQuizTemplate,
  answers: Record<string, BmiQuizAnswerValue>,
): BmiQuizField[] {
  // TES-1658 — a follow-up of a hidden question is hidden too, whatever its
  // parent's stale answer says (methodsTried hides for a transfer patient, and
  // weightMedHistory follows it). Parents precede their follow-ups.
  const shown = new Set<string>();
  const keys = new Set(template.fields.map((f) => f.key));
  return template.fields.filter((f) => {
    const visible = isShown(f);
    if (visible) shown.add(f.key);
    return visible;
  });

  function isShown(f: BmiQuizField): boolean {
    if (!f.showWhen) return true;
    if (keys.has(f.showWhen.parentKey) && !shown.has(f.showWhen.parentKey)) {
      return false;
    }
    const parent = answers[f.showWhen.parentKey];
    if (!parent) return false;
    const triggers = f.showWhen.triggerValues ?? [
      ...(f.showWhen.triggerValue ? [f.showWhen.triggerValue] : []),
    ];
    if (triggers.length === 0) return false;
    if (Array.isArray(parent)) return parent.some((v) => triggers.includes(v));
    return triggers.includes(parent);
  }
}

// Cross-field validation shared by every quiz intake path (the public web
// submit and the in-app F-01 submit): every VISIBLE required field must have
// a non-empty answer. Returns the missing field keys ([] = valid). `required`
// defaults to true via the schema; explicit `optional: true` marks an
// informational follow-up the patient can skip.
/**
 * Is this step informational rather than a question?
 *
 * The one predicate both the web renderer and the API consult. An information
 * step never blocks the forward button and never contributes an answer.
 */
export function isInformationStep(field: BmiQuizField): boolean {
  return field.kind === "information_step";
}

export function missingRequiredBmiQuizAnswers(
  template: BmiQuizTemplate,
  answers: Record<string, BmiQuizAnswerValue>,
): string[] {
  const missing: string[] = [];
  for (const field of visibleBmiQuizFields(template, answers)) {
    // An information step is not a question: it can never be "missing".
    if (isInformationStep(field)) continue;
    const v = answers[field.key];
    const empty =
      v === undefined ||
      v === null ||
      (typeof v === "string" && v.trim() === "") ||
      (Array.isArray(v) && v.length === 0);
    const isRequired = field.required !== false && !field.optional;
    if (isRequired && empty) {
      missing.push(field.key);
      continue;
    }
    // A multi_select with a declared cap must respect it. The cap used to
    // live only in the help text, so "Välj högst tre alternativ" was a
    // request the server had no way to hold anyone to.
    if (
      field.kind === "multi_select" &&
      field.maxSelections !== undefined &&
      Array.isArray(v) &&
      v.length > field.maxSelections
    ) {
      missing.push(field.key);
      continue;
    }
    // A numeric answer that is present must parse and sit inside its bounds
    // — an out-of-range weight would silently corrupt the BMI indication.
    if (field.kind === "numeric" && !empty && typeof v === "string") {
      const n = parseBmiQuizNumber(v);
      if (
        n === null ||
        (field.numeric && (n < field.numeric.min || n > field.numeric.max))
      ) {
        missing.push(field.key);
      }
    }
  }
  return missing;
}

// Comma-tolerant numeric parse shared by validators and the BMI computation.
export function parseBmiQuizNumber(raw: string): number | null {
  const n = Number.parseFloat(raw.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

// F-01 v2 — BMI from the two required inputs; null when either is absent or
// unparseable (the normal flow can't get here — both fields are required).
export function computeBmiFromAnswers(
  answers: Record<string, BmiQuizAnswerValue>,
): number | null {
  const w = typeof answers.weightKg === "string" ? answers.weightKg : null;
  const h = typeof answers.heightCm === "string" ? answers.heightCm : null;
  if (!w || !h) return null;
  const kg = parseBmiQuizNumber(w);
  const cm = parseBmiQuizNumber(h);
  if (kg === null || cm === null || kg <= 0 || cm <= 0) return null;
  const m = cm / 100;
  return kg / (m * m);
}

// TES-1066 — the BMI every F-01 threshold is compared against: one decimal,
// half up, i.e. exactly the number the patient is shown. Emelie's bands are
// written in one decimal ("25,0–26,9"), so a raw 24.96 — shown as 25,0 —
// belongs to 25,0–26,9, and a raw 26.96 — shown as 27,0 — to 27 and over.
// The 1e-9 absorbs binary representation error: 51.84 kg at 160 cm is BMI
// 20.25 exactly, computed as 20.2499999…, and must still round to 20.3.
export function roundBmi(bmi: number): number {
  return Math.round(bmi * 10 + 1e-9) / 10;
}

// ---------------------------------------------------------------------------
// F-01 v2 — the SUIT question set from the form PDF: weight/height first
// (required — the whole result logic is BMI-banded), then the twelve-question
// suitability journey. howFoundUs stays as an optional attribution tail
// (defined purpose: marketing source; flagged in docs/forms-clinical-flags.md
// together with the dropped v1 extras activityLevel/hardestPart/
// speedPreference).
// ---------------------------------------------------------------------------

// TES-430 (Ö-86) — SUIT_REPRODUCTIVE_STATUS, the one early safety question
// the canonical F-01 carries. Values are the stable ids the stop rule and
// the seven-day rule key on; copy lives on the field.
export const REPRODUCTIVE_STATUS_QUESTION_ID = "SUIT_REPRODUCTIVE_STATUS";
export const REPRODUCTIVE_STATUS_KEY = "reproductiveStatus";
/** The answers that stop a treatment start (Resultat B). */
export const REPRODUCTIVE_STATUS_STOP_VALUES = [
  "pregnant",
  "breastfeeding",
  "trying",
] as const;
/** The answer that means information + a conversation, never a refusal. */
export const REPRODUCTIVE_STATUS_DIALOG_VALUE = "planning";

/** SUIT_REPRODUCTIVE_STATUS as currently authored — one source for every surface. */
export function reproductiveStatusField(): BmiQuizField {
  const field = bmiQuizV2.fields.find((f) => f.key === REPRODUCTIVE_STATUS_KEY);
  if (!field) throw new Error("bmiQuizV2 has no reproductiveStatus field");
  return field;
}

/**
 * TES-524 — a stored reproductive-status answer as one of today's option
 * values, or null when there is none (a v4 F-01, or a value the question no
 * longer offers). Never a guess: F-02 then asks the question itself.
 */
export function readReproductiveStatus(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const offered = (reproductiveStatusField().options ?? []).map((o) => o.value);
  return offered.includes(raw) ? raw : null;
}

// TES-524 — F-01 Q12 read as ONE data point wherever it is reused (F-02's
// seed, the doctor's patient view). The question id is the canonical anchor
// kept per TES-476; the values are stable ids, not copy.
export const DESIRED_OUTCOME_QUESTION_ID = "SUIT_DESIRED_OUTCOME";
export const DESIRED_OUTCOME_KEY = "desiredOutcome";

/** The retired values and what they read as today (merge, never rewrite). */
const RETIRED_DESIRED_OUTCOME: Readonly<Record<string, string>> = {
  energy: "energy_strength",
  strength: "energy_strength",
};

/** Q12 as currently authored — label + option list, one source for every surface. */
export function desiredOutcomeField(): BmiQuizField {
  const field = bmiQuizV2.fields.find((f) => f.key === DESIRED_OUTCOME_KEY);
  if (!field) throw new Error("bmiQuizV2 has no desiredOutcome field");
  return field;
}

/**
 * A stored Q12 answer (any version) as today's option values: retired values
 * are READ as the option that absorbed them, unknown values dropped, order
 * kept, no duplicates. The stored answer itself is never touched — the
 * submission's templateVersion is what makes the original readable.
 */
export function readDesiredOutcome(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const offered = new Set(
    (desiredOutcomeField().options ?? []).map((o) => o.value),
  );
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== "string") continue;
    const today = RETIRED_DESIRED_OUTCOME[v] ?? v;
    if (offered.has(today) && !out.includes(today)) out.push(today);
  }
  return out;
}

// TES-1319 — SUIT_DESIRED_SUPPORT, F-01 v11 ("KANONISKT TILLÄGG 2026-10-13 —
// ÖNSKAT STÖD I F-01"). What support she believes would help most, max two.
// The option values are the canonical codes verbatim: they are the stable
// identifiers the doc names, not copy.
//
// It is a preference, not a finding: "Det får inte omvandlas till diagnos,
// journalpåstående eller medicinskt fynd." So it routes nothing, flags nothing
// and stays out of the journal (allowlisted there). The patient memory takes
// the chosen codes only, never OTHER or its free text (TES-1321,
// f01-memory.ts). Neither the codes nor the "Annat" free text reach the
// AI readback (bmiQuizAsTemplateContent) until Ö-534 says otherwise. TES-1542
// (Ö-812): behind AI_OVERVIEW_DESIRED_SUPPORT_ENABLED, the doctor's form
// overview alone adds the codes as labelled wishes; the free text never goes.
export const DESIRED_SUPPORT_QUESTION_ID = "SUIT_DESIRED_SUPPORT";
export const DESIRED_SUPPORT_KEY = "desiredSupport";
export const DESIRED_SUPPORT_OTHER_KEY = "desiredSupportOther";
export const DESIRED_SUPPORT_MAX_SELECTIONS = 2;
// Both keys F-01 v11 added; an answer set without them was given against v10
// or earlier, and the older instruments below never carry them.
export const DESIRED_SUPPORT_KEYS: ReadonlySet<string> = new Set([
  DESIRED_SUPPORT_KEY,
  DESIRED_SUPPORT_OTHER_KEY,
]);

/** SUIT_DESIRED_SUPPORT as currently authored: label + option list. */
export function desiredSupportField(): BmiQuizField {
  const field = bmiQuizV2.fields.find((f) => f.key === DESIRED_SUPPORT_KEY);
  if (!field) throw new Error("bmiQuizV2 has no desiredSupport field");
  return field;
}

/**
 * TES-1322 — a stored SUIT_DESIRED_SUPPORT answer as the codes the quiz
 * offers: unknown values dropped, order kept, no duplicates. An answer set
 * from v10 or earlier has no such key and reads as [] — never asked, which is
 * not the same as answering nothing, so callers render nothing for it.
 */
export function readDesiredSupport(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const offered = new Set(
    (desiredSupportField().options ?? []).map((o) => o.value),
  );
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v === "string" && offered.has(v) && !out.includes(v)) {
      out.push(v);
    }
  }
  return out;
}

// TES-733 — the prescription-transfer routing question, back in F-01 (Emelie
// 2026-09-16: "valet av receptövertag görs i F-01"; the F-01 doc's
// "MVP-BESLUT — RECEPTÖVERTAG OCH ORDNINGSFÖLJD"). Five options, verbatim.
// ONLY `ja_fortsatt` ("Ja, och jag vill fortsätta behandlingen hos Ella")
// routes: it is the patient's active choice of the transfer track. The other
// four are stored for the doctor and continue the ordinary journey. The
// stored v3–v7 values `ja`/`osaker` were given against a different question
// and route nowhere (Ö-154, Ö-158).
//
// No information step follows it:
// "Ingen separat informations- eller förberedelsesida visas efter F-01."
// The routing block is part of her feedback (health-profile-summary.ts), and
// the prep page comes after F-02.
export const PRESCRIPTION_TRANSFER_KEY_F01 = "prescriptionTransfer";
export const PRESCRIPTION_TRANSFER_ACTIVE_CHOICE = "ja_fortsatt";

export const PRESCRIPTION_TRANSFER_QUESTION: BmiQuizField = {
  key: PRESCRIPTION_TRANSFER_KEY_F01,
  kind: "single_select",
  label: {
    sv: "Har du just nu ett giltigt recept eller en pågående läkemedelsbehandling för viktminskning?",
    en: "Do you currently have a valid prescription or ongoing medication treatment for weight loss?",
  },
  required: true,
  options: [
    { value: "nej", label: { sv: "Nej", en: "No" } },
    {
      value: PRESCRIPTION_TRANSFER_ACTIVE_CHOICE,
      label: {
        sv: "Ja, och jag vill fortsätta behandlingen hos Ella",
        en: "Yes, and I want to continue the treatment with Ella",
      },
    },
    {
      value: "ja_ej_startat",
      label: {
        sv: "Ja, men jag har inte börjat behandlingen ännu",
        en: "Yes, but I have not started the treatment yet",
      },
    },
    {
      value: "uppehall",
      label: {
        sv: "Jag har nyligen gjort uppehåll eller avslutat",
        en: "I recently paused or ended treatment",
      },
    },
    { value: "osaker", label: { sv: "Jag är osäker", en: "I am not sure" } },
  ],
};

// TES-1164 (Ö-383, Emelie 2026-10-08) — the two early medical safety
// questions, verbatim from the F-01 document's "1B. Tidiga medicinska
// säkerhetsfrågor":
//   "Dessa två frågor ställs en gång i F-01. Svaren sparas på patientärendet
//   och återanvänds i F-02, admin och läkarens beslutsunderlag. F-02 får inte
//   ställa samma frågor igen."
// "Ja" stops (f01-result.ts, §13/§14 of the canonical result copy). The
// unsure answers continue with a doctor flag (risk-codes.v1.ts), never a
// stop. The values are stable ids, not copy.
export const TYPE_1_DIABETES_QUESTION_ID = "SUIT_TYPE_1_DIABETES";
export const TYPE_1_DIABETES_KEY = "type1Diabetes";
export const MTC_MEN2_QUESTION_ID = "SUIT_MTC_MEN2";
export const MTC_MEN2_KEY = "mtcMen2";
/** The answer to either question that stops a treatment start. */
export const F01_SAFETY_STOP_VALUE = "yes";
/** The answers that continue with a doctor flag, per question. */
export const TYPE_1_DIABETES_UNSURE_VALUE = "unsure";
export const MTC_MEN2_UNKNOWN_VALUE = "dont_know";

export const TYPE_1_DIABETES_QUESTION: BmiQuizField = {
  key: TYPE_1_DIABETES_KEY,
  kind: "single_select",
  label: {
    sv: "Har du fått diagnosen diabetes typ 1?",
    en: "Have you been diagnosed with type 1 diabetes?",
  },
  required: true,
  options: [
    { value: F01_SAFETY_STOP_VALUE, label: { sv: "Ja", en: "Yes" } },
    { value: "no", label: { sv: "Nej", en: "No" } },
    {
      value: TYPE_1_DIABETES_UNSURE_VALUE,
      label: { sv: "Jag är osäker", en: "I'm not sure" },
    },
  ],
};

// "Ingen separat hjälptext."
export const MTC_MEN2_QUESTION: BmiQuizField = {
  key: MTC_MEN2_KEY,
  kind: "single_select",
  label: {
    sv: "Har du eller någon av dina biologiska föräldrar, syskon eller barn fått diagnosen medullär sköldkörtelcancer (MTC) eller multipel endokrin neoplasi typ 2 (MEN 2)?",
    en: "Have you or any of your biological parents, siblings or children been diagnosed with medullary thyroid cancer (MTC) or multiple endocrine neoplasia type 2 (MEN 2)?",
  },
  required: true,
  options: [
    { value: F01_SAFETY_STOP_VALUE, label: { sv: "Ja", en: "Yes" } },
    { value: "no", label: { sv: "Nej", en: "No" } },
    {
      value: MTC_MEN2_UNKNOWN_VALUE,
      label: { sv: "Jag vet inte", en: "I don't know" },
    },
  ],
};

export type F01SafetyAnswer = "yes" | "no" | "unsure";

/**
 * TES-1164 — one of the two safety answers as stored, read as yes / no /
 * unsure ("Jag vet inte" reads as unsure), or null when the F-01 never asked
 * it: an answer set from before v10, or from an app build that was served
 * v9 (`CLIENT_CONTRACT_CHANGES.f01SafetyQuestions`). Null is "not asked",
 * never "no".
 */
export function readF01SafetyAnswer(
  key: typeof TYPE_1_DIABETES_KEY | typeof MTC_MEN2_KEY,
  raw: unknown,
): F01SafetyAnswer | null {
  if (raw === F01_SAFETY_STOP_VALUE) return "yes";
  if (raw === "no") return "no";
  const unsure =
    key === TYPE_1_DIABETES_KEY
      ? TYPE_1_DIABETES_UNSURE_VALUE
      : MTC_MEN2_UNKNOWN_VALUE;
  return raw === unsure ? "unsure" : null;
}

// TES-1533 — this literal is v11, frozen: the instrument every answer set
// stamped 2–11 is read against. The current quiz, `bmiQuizV2` (v12), is
// built from it below.
export const bmiQuizV11: BmiQuizTemplate = {
  key: "bmi-quiz",
  // Bumped to 3 by "Hälsoformulär v3" §4.2: the prescription-transfer branch
  // changes the FIELD SET, and templateVersion exists so stored answers stay
  // interpretable as the template evolves. Adding a required question to
  // version 2 in place would have made "version 2" mean two different
  // instruments depending on when the lead was captured.
  //
  // Side effect worth having: the web draft key is derived from the version, so
  // a quiz begun against the old field set is not resumed against the new one.
  //
  // Bumped to 4 by the canonical Q12 decision 2026-09-03 (TES-476), for
  // exactly the reason stated above: the SUIT_DESIRED_OUTCOME option list is
  // replaced (two values retired, two added, one merged), so "version 3" and
  // "version 4" are different instruments and a stored answer needs to say
  // which one it was given against.
  // TES-430 — v5 adds SUIT_REPRODUCTIVE_STATUS (required). An answer set
  // without it was given against v4 and stays readable as such.
  // TES-578 — v6 REMOVES the prescription-transfer question (and its
  // information step) while F-07 is paused. An answer set with
  // prescriptionTransfer was given against v3–v5; v6 never asks, and
  // derivePatientGroup treats the missing answer as new treatment.
  // TES-607 — v7 REMOVES the SUIT_CURRENT_COMORBIDITY question v6 asked in
  // the 27–29.9 band (F-01 does not ask about
  // comorbidity; BMI ≥ 27 is Resultat A). A stored v6 answer is ignored.
  // TES-631 — v8 REMOVES the prescription-transfer question: it
  // is asked in F-02 instead. A stored `prescriptionTransfer` answer was
  // given against v3–v7 and stays readable as such; v8 never asks it.
  // TES-733 — v9 asks it AGAIN, with the five canonical options (Emelie
  // 2026-09-16). The value set is new: only `ja_fortsatt` routes. A stored
  // v3–v7 `ja`/`osaker` is a different question's answer and routes nowhere.
  // TES-1164 — v10 adds the two safety questions (type 1 diabetes, MTC/MEN 2),
  // required. An answer set without them was given against v9 or earlier,
  // and "not asked" stops and flags nothing.
  // TES-1319 — v11 adds SUIT_DESIRED_SUPPORT (required, max two) and its
  // optional "Annat" free text, between SUIT_IMPACT and SUIT_DESIRED_OUTCOME.
  // An answer set without it was given against v10 or earlier; nothing is
  // re-validated on stored rows, and the answer routes nothing.
  version: 11,
  fields: [
    {
      key: "weightKg",
      kind: "numeric",
      unit: "kg",
      numeric: { min: 30, max: 350 },
      label: { sv: "Vad väger du idag?", en: "What do you weigh today?" },
      help: {
        sv: "Om du inte vet exakt, ange din bästa uppskattning.",
        en: "If you don't know exactly, give your best estimate.",
      },
      required: true,
    },
    {
      key: "heightCm",
      kind: "numeric",
      unit: "cm",
      numeric: { min: 120, max: 230 },
      label: { sv: "Hur lång är du?", en: "How tall are you?" },
      // TES-1234 (Emelie 2026-10-11) — her line "under vikt och längd",
      // verbatim; height is the second of the pair, so it sits under both
      // in the app's one-page form. Served by GET /f01/quiz, so every app
      // build shows it after an API deploy; the web bundles it.
      help: {
        sv: "Vikt och längd används för att beräkna ditt BMI och ge dig rätt återkoppling och nästa steg. BMI är inte hela bilden, men fungerar som en första utgångspunkt.",
        en: "Weight and height are used to calculate your BMI and give you the right feedback and next step. BMI is not the whole picture, but it works as a first starting point.",
      },
      required: true,
    },
    // TES-430 (Ö-86) — §4.2 step 2, the canonical F-01
    // question SUIT_REPRODUCTIVE_STATUS, verbatim from the F-01 document.
    // Pregnant / breastfeeding / trying = early stop before a treatment
    // start (Resultat B, see f01-early-stop); planning = information and a
    // conversation, never a refusal; unsure = the doctor's call. Step 1 (age)
    // is not a question — it comes from BankID and gates F-01 itself.
    {
      key: "reproductiveStatus",
      kind: "single_select",
      label: {
        sv: "Vad stämmer just nu för dig?",
        en: "What is true for you right now?",
      },
      required: true,
      options: [
        {
          value: "not_pregnant",
          label: { sv: "Inte gravid", en: "Not pregnant" },
        },
        { value: "pregnant", label: { sv: "Gravid", en: "Pregnant" } },
        { value: "breastfeeding", label: { sv: "Ammar", en: "Breastfeeding" } },
        {
          value: "trying",
          label: { sv: "Försöker bli gravid", en: "Trying to get pregnant" },
        },
        {
          value: "planning",
          label: { sv: "Planerar graviditet", en: "Planning a pregnancy" },
        },
        { value: "unsure", label: { sv: "Osäker", en: "Unsure" } },
        {
          value: "prefer_not_to_say",
          label: { sv: "Vill inte ange", en: "Prefer not to say" },
        },
      ],
    },
    // TES-1164 — directly after the reproductive question: the F-01
    // document lists "1B. Tidiga medicinska säkerhetsfrågor" right after
    // SUIT_REPRODUCTIVE_STATUS, under "Tidiga säkerhets- och
    // behörighetskontroller", and before Q1. The early safety questions stay
    // together, ahead of the routing question.
    TYPE_1_DIABETES_QUESTION,
    MTC_MEN2_QUESTION,
    // TES-733 — after the reproductive question, before the assessment
    // questions: it changes which journey she is on, so it is asked before
    // she has answered a page she would otherwise be re-routed out of.
    PRESCRIPTION_TRANSFER_QUESTION,
    {
      key: "howLongTried",
      kind: "single_select",
      label: {
        sv: "Hur länge har vikten varit en utmaning för dig?",
        en: "How long has weight been a challenge for you?",
      },
      required: true,
      options: [
        {
          value: "less_than_6m",
          label: { sv: "Mindre än 6 månader", en: "Less than 6 months" },
        },
        { value: "6_to_12m", label: { sv: "6–12 månader", en: "6–12 months" } },
        { value: "1_to_3y", label: { sv: "1–3 år", en: "1–3 years" } },
        {
          value: "more_than_3y",
          label: { sv: "Mer än 3 år", en: "More than 3 years" },
        },
        {
          value: "most_of_life",
          label: {
            sv: "Större delen av mitt liv",
            en: "Most of my life",
          },
        },
        { value: "unsure", label: { sv: "Jag är osäker", en: "I'm not sure" } },
      ],
    },
    {
      key: "changeContext",
      kind: "multi_select",
      label: {
        sv: "När upplevde du att vikten förändrades eller blev svårare att påverka?",
        en: "When did you feel that your weight changed or became harder to influence?",
      },
      required: true,
      options: [
        {
          value: "after_pregnancy",
          label: { sv: "Efter graviditet", en: "After pregnancy" },
        },
        {
          value: "with_age",
          label: { sv: "När jag blev äldre", en: "As I got older" },
        },
        {
          value: "hormonal_changes",
          label: {
            sv: "I samband med hormonella förändringar",
            en: "Around hormonal changes",
          },
        },
        {
          value: "stress_period",
          label: {
            sv: "Under eller efter en stressig period",
            en: "During or after a stressful period",
          },
        },
        {
          value: "illness_or_medication",
          label: {
            sv: "Efter sjukdom eller medicinering",
            en: "After illness or medication",
          },
        },
        {
          value: "changed_routines",
          label: {
            sv: "Efter förändrade rutiner eller mindre rörelse",
            en: "After changed routines or less movement",
          },
        },
        {
          value: "long_standing",
          label: {
            sv: "Vikten har länge varit en utmaning",
            en: "Weight has long been a challenge",
          },
        },
        {
          value: "dont_know",
          label: { sv: "Jag vet inte", en: "I don't know" },
        },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: "methodsTried",
      kind: "multi_select",
      label: {
        sv: "Vad har du tidigare provat?",
        en: "What have you tried before?",
      },
      required: true,
      options: [
        {
          value: "diet_program",
          label: {
            sv: "Kostprogram eller kaloriräkning",
            en: "Diet programmes or calorie counting",
          },
        },
        {
          value: "diets_fasting",
          label: { sv: "Dieter eller fasta", en: "Diets or fasting" },
        },
        {
          value: "exercise",
          label: {
            sv: "Träning eller mer vardagsrörelse",
            en: "Exercise or more everyday movement",
          },
        },
        {
          value: "professional_support",
          label: {
            sv: "Stöd från vårdpersonal eller coach",
            en: "Support from healthcare staff or a coach",
          },
        },
        {
          value: "medication",
          label: {
            sv: "Receptbelagd viktbehandling",
            en: "Prescription weight treatment",
          },
        },
        {
          value: "no_structured_attempts",
          label: {
            sv: "Jag har inte gjort något strukturerat försök",
            en: "I haven't made a structured attempt",
          },
        },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: "weightMedHistory",
      kind: "single_select",
      label: {
        sv: "Vad beskriver din erfarenhet bäst?",
        en: "What best describes your experience?",
      },
      required: true,
      showWhen: { parentKey: "methodsTried", triggerValue: "medication" },
      options: [
        {
          value: "using_today",
          label: {
            sv: "Jag använder behandling idag",
            en: "I'm on treatment today",
          },
        },
        {
          value: "past_good_effect",
          label: {
            sv: "Jag använde tidigare med god effekt",
            en: "I used it before with good effect",
          },
        },
        {
          value: "stopped_side_effects",
          label: {
            sv: "Jag slutade på grund av besvär eller biverkningar",
            en: "I stopped because of problems or side effects",
          },
        },
        {
          value: "stopped_other_reason",
          label: {
            sv: "Jag slutade av annan anledning",
            en: "I stopped for another reason",
          },
        },
        {
          value: "dont_remember",
          label: {
            sv: "Jag minns inte namnet eller detaljerna",
            en: "I don't remember the name or details",
          },
        },
      ],
    },
    {
      key: "bodySignals",
      kind: "multi_select",
      label: {
        sv: "Vad känner du igen kring hunger, mättnad och sug?",
        en: "What do you recognise around hunger, fullness and cravings?",
      },
      required: true,
      options: [
        {
          value: "often_hungry",
          label: {
            sv: "Jag är ofta hungrig trots att jag ätit",
            en: "I'm often hungry even after eating",
          },
        },
        {
          value: "rarely_full",
          label: {
            sv: "Jag blir sällan ordentligt mätt",
            en: "I rarely feel properly full",
          },
        },
        {
          value: "food_thoughts",
          label: {
            sv: "Jag tänker mycket på mat",
            en: "I think about food a lot",
          },
        },
        {
          value: "strong_cravings",
          label: { sv: "Jag får starkt sug", en: "I get strong cravings" },
        },
        {
          value: "hard_to_stop",
          label: {
            sv: "Jag har svårt att sluta när jag börjat äta",
            en: "It's hard to stop once I've started eating",
          },
        },
        {
          value: "hard_to_feel_signals",
          label: {
            sv: "Jag har svårt att känna hunger eller mättnad",
            en: "It's hard to feel hunger or fullness",
          },
        },
        {
          value: "none_of_these",
          label: { sv: "Inget av detta", en: "None of these" },
        },
      ],
    },
    {
      key: "biggestObstacles",
      kind: "multi_select",
      label: {
        sv: "Vad brukar göra förändring svårt för dig?",
        en: "What usually makes change hard for you?",
      },
      help: { sv: "Välj upp till 3.", en: "Pick up to 3." },
      required: true,
      options: [
        {
          value: "regain",
          label: {
            sv: "Jag går ner men går ofta upp igen",
            en: "I lose weight but often regain it",
          },
        },
        {
          value: "strong_start_fades",
          label: {
            sv: "Jag börjar starkt men tappar över tid",
            en: "I start strong but fade over time",
          },
        },
        {
          value: "works_until_stress",
          label: {
            sv: "Det fungerar tills livet blir stressigt",
            en: "It works until life gets stressful",
          },
        },
        {
          value: "routines_hard",
          label: {
            sv: "Jag har svårt att få rutiner att fungera i vardagen",
            en: "Routines are hard to make work day to day",
          },
        },
        {
          value: "know_but_cant_do",
          label: {
            sv: "Jag vet ofta vad jag vill göra men får inte till det konsekvent",
            en: "I often know what to do but can't do it consistently",
          },
        },
        {
          value: "hunger_cravings",
          label: {
            sv: "Hunger eller sug tar över",
            en: "Hunger or cravings take over",
          },
        },
        {
          value: "self_criticism",
          label: {
            sv: "Jag blir hård mot mig själv när det inte blir som planerat",
            en: "I'm hard on myself when things don't go to plan",
          },
        },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: "emotionalEating",
      kind: "single_select",
      label: {
        sv: "Hjälper mat dig ibland att hantera stress, trötthet, känslor eller behov av paus?",
        en: "Does food sometimes help you handle stress, tiredness, emotions or the need for a break?",
      },
      required: true,
      options: [
        { value: "yes_often", label: { sv: "Ja, ofta", en: "Yes, often" } },
        {
          value: "yes_sometimes",
          label: { sv: "Ja, ibland", en: "Yes, sometimes" },
        },
        { value: "no_rarely", label: { sv: "Nej, sällan", en: "No, rarely" } },
        {
          value: "dont_know",
          label: { sv: "Jag vet inte", en: "I don't know" },
        },
      ],
    },
    {
      key: "lifeStage",
      kind: "multi_select",
      label: {
        sv: "Finns det någon livsfas som känns relevant för din viktresa?",
        en: "Is there a life stage that feels relevant to your weight journey?",
      },
      required: false,
      optional: true,
      options: [
        {
          value: "regular_periods",
          label: { sv: "Regelbunden mens", en: "Regular periods" },
        },
        {
          value: "irregular_periods",
          label: { sv: "Oregelbunden mens", en: "Irregular periods" },
        },
        {
          value: "after_pregnancy",
          label: { sv: "Efter graviditet", en: "After pregnancy" },
        },
        {
          value: "perimenopause",
          label: {
            sv: "Perimenopaus eller klimakteriet",
            en: "Perimenopause or menopause",
          },
        },
        {
          value: "after_menopause",
          label: { sv: "Efter klimakteriet", en: "After menopause" },
        },
        {
          value: "prefer_not_to_say",
          label: { sv: "Inget jag vill ange", en: "Prefer not to say" },
        },
        { value: "unsure", label: { sv: "Jag är osäker", en: "I'm not sure" } },
      ],
    },
    {
      key: "impactDegree",
      kind: "single_select",
      label: {
        sv: "Hur mycket påverkar vikten din hälsa eller vardag idag?",
        en: "How much does weight affect your health or everyday life today?",
      },
      required: true,
      options: [
        {
          value: "very_little",
          label: { sv: "Väldigt lite", en: "Very little" },
        },
        { value: "some", label: { sv: "En del", en: "Some" } },
        { value: "a_lot", label: { sv: "Mycket", en: "A lot" } },
        {
          value: "very_much",
          label: { sv: "Väldigt mycket", en: "Very much" },
        },
      ],
    },
    {
      // TES-1319 — SUIT_DESIRED_SUPPORT; see DESIRED_SUPPORT_QUESTION_ID.
      // Required: "Jag vet inte ännu" and "Annat" let her answer without
      // picking a ready-made form of support.
      key: DESIRED_SUPPORT_KEY,
      kind: "multi_select",
      label: {
        sv: "Vilket stöd tror du skulle göra störst skillnad för dig just nu?",
        en: "What support do you think would make the biggest difference for you right now?",
      },
      help: { sv: "Välj högst två alternativ.", en: "Pick at most two." },
      required: true,
      maxSelections: DESIRED_SUPPORT_MAX_SELECTIONS,
      options: [
        {
          value: "MEDICATION_SUPPORT",
          label: {
            sv: "Stöd med läkemedelsbehandling för min vikt",
            en: "Support with medication treatment for my weight",
          },
        },
        {
          value: "APPETITE_SIGNALS",
          label: {
            sv: "Hjälp med hunger, mättnad och sug",
            en: "Help with hunger, fullness and cravings",
          },
        },
        {
          value: "EVERYDAY_FOOD",
          label: {
            sv: "Stöd kring mat som fungerar i min vardag",
            en: "Support with food that works in my everyday life",
          },
        },
        {
          value: "MOVEMENT_EXERCISE",
          label: {
            sv: "Stöd kring rörelse och träning",
            en: "Support with movement and exercise",
          },
        },
        {
          value: "SUSTAINABLE_HABITS",
          label: {
            sv: "Hjälp att skapa vanor och rutiner som håller",
            en: "Help building habits and routines that last",
          },
        },
        {
          value: "STRESS_EMOTIONS_SELF_CRITICISM",
          label: {
            sv: "Stöd kring stress, känslor eller självkritik",
            en: "Support with stress, emotions or self-criticism",
          },
        },
        {
          value: "STRUCTURE_FOLLOW_UP_DIRECTION",
          label: {
            sv: "Struktur, uppföljning och hjälp att hålla riktningen",
            en: "Structure, follow-up and help keeping on course",
          },
        },
        {
          // Ö-531 default: exclusive. "Jag vet inte ännu" next to a concrete
          // choice contradicts itself.
          value: "NOT_SURE_YET",
          exclusive: true,
          label: { sv: "Jag vet inte ännu", en: "I don't know yet" },
        },
        { value: "OTHER", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      // "Om OTHER väljs visas ett kort frivilligt fritextfält."
      key: DESIRED_SUPPORT_OTHER_KEY,
      kind: "short_text",
      label: {
        sv: "Berätta gärna vilket stöd du tänker på",
        en: "Feel free to tell us what support you have in mind",
      },
      required: false,
      optional: true,
      showWhen: { parentKey: DESIRED_SUPPORT_KEY, triggerValue: "OTHER" },
    },
    {
      // Q12 / SUIT_DESIRED_OUTCOME. The canonical decision 2026-09-03
      // (TES-476) replaced the option list wholesale. The question_id and the
      // `desiredOutcome` key are deliberately unchanged.
      //
      // On the VALUES: they are stable identifiers, not copy, so the widened
      // wording on several options keeps its original value — an answer given
      // last month still means what it meant. Two are genuinely new
      // (`appearance`, `maintain_result`), and `energy_strength` MERGES what
      // used to be `energy` + `strength`. Those two legacy values are no
      // longer offered but are never rewritten: stored answers keep them, and
      // `templateVersion` on the submission says which instrument produced
      // them. Nothing migrates.
      key: "desiredOutcome",
      kind: "multi_select",
      label: {
        sv: "Vad är viktigast för dig när det gäller din vikt just nu?",
        en: "What matters most to you about your weight right now?",
      },
      help: { sv: "Välj högst tre alternativ.", en: "Pick at most three." },
      required: true,
      maxSelections: 3,
      options: [
        {
          value: "long_term_health",
          label: {
            sv: "Förbättra min hälsa nu eller på längre sikt",
            en: "Improve my health now or in the longer term",
          },
        },
        {
          value: "energy_strength",
          label: {
            sv: "Få mer energi, ork eller styrka",
            en: "Get more energy, stamina or strength",
          },
        },
        {
          value: "less_pain",
          label: {
            sv: "Minska smärta, belastning eller andra fysiska besvär",
            en: "Reduce pain, strain or other physical problems",
          },
        },
        {
          value: "everyday_freedom",
          label: {
            sv: "Få större frihet i vardagen",
            en: "Have more freedom in everyday life",
          },
        },
        {
          value: "body_confidence",
          label: {
            sv: "Känna mig mer trygg och bekväm i min kropp",
            en: "Feel more secure and comfortable in my body",
          },
        },
        {
          value: "appearance",
          label: {
            sv: "Trivas bättre med mitt utseende",
            en: "Feel better about how I look",
          },
        },
        {
          value: "feel_like_myself",
          label: {
            sv: "Känna mig mer som mig själv",
            en: "Feel more like myself",
          },
        },
        {
          value: "maintain_result",
          label: {
            sv: "Behålla ett resultat jag redan har uppnått",
            en: "Keep a result I have already achieved",
          },
        },
        {
          value: "other",
          label: { sv: "Annat – skriv gärna", en: "Something else — tell us" },
        },
      ],
    },
    {
      // "Vid 'Annat' ska en frivillig fritextruta visas." Same shape as the
      // howFoundUs follow-up below — no new mechanism.
      key: "desiredOutcomeOther",
      kind: "short_text",
      label: { sv: "Berätta gärna", en: "Tell us more" },
      required: false,
      optional: true,
      showWhen: { parentKey: "desiredOutcome", triggerValue: "other" },
    },
    {
      key: "howFoundUs",
      kind: "single_select",
      label: { sv: "Hur hittade du oss?", en: "How did you find us?" },
      required: false,
      optional: true,
      options: [
        { value: "google", label: { sv: "Google", en: "Google" } },
        { value: "instagram", label: { sv: "Instagram", en: "Instagram" } },
        { value: "tiktok", label: { sv: "TikTok", en: "TikTok" } },
        {
          value: "friend",
          label: { sv: "Vän eller familj", en: "Friend or family" },
        },
        {
          value: "doctor",
          label: { sv: "Vård/läkare", en: "Healthcare/doctor" },
        },
        { value: "podcast", label: { sv: "Podcast", en: "Podcast" } },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: "howFoundUsOther",
      kind: "short_text",
      label: { sv: "Berätta gärna hur", en: "Tell us how" },
      required: false,
      optional: true,
      showWhen: { parentKey: "howFoundUs", triggerValue: "other" },
    },
  ],
};

// ---------------------------------------------------------------------------
// TES-1533 (Ö-809) — v12 asks the weight in the register's words.
//
// Emelie 2026-10-21 (Ö-809): "Ja. Samma mätvärde WEIGHT_KG använder exakt ”Vad väger du? (kg)” även i F-01/F-02. Skapa spårbara nya fråge-/formulärversioner utan att ändra originalsvar; obligatoriska uppgifter förblir obligatoriska."
//
// v11 asked "Vad väger du idag?". v12 is v11 with that one label replaced by
// WEIGHT_KG's copy (question-register.v1.ts); key, bounds, help and
// requiredness are v11's. The words changed, so it is a new version: an
// answer stamped 11 or lower was given to "Vad väger du idag?", and the
// readback shows it with that question (f01QuizForVersion).
//
// The field is not bound to WEIGHT_KG: F-01 is not a FormTemplateContent, and
// its bounds (30–350 kg) are F-01's own, not the register's 1–400.
// f01-weight-v12.spec.ts holds the label to the register's copy.
export const F01_WEIGHT_KEY = "weightKg";

function askedAsWeightKg(field: BmiQuizField): BmiQuizField {
  if (field.key !== F01_WEIGHT_KEY) return field;
  return {
    ...field,
    label: { sv: "Vad väger du? (kg)", en: "What do you weigh? (kg)" },
  };
}

// TES-1658 — v12 is frozen here: the instrument every answer set stamped 12
// is read against. The current quiz, `bmiQuizV2` (v13), follows.
export const bmiQuizV12: BmiQuizTemplate = {
  ...bmiQuizV11,
  version: 12,
  fields: bmiQuizV11.fields.map(askedAsWeightKg),
};

// ---------------------------------------------------------------------------
// TES-1658 — v13, Emelie's "GODKÄNT PRODUKTBESLUT 2026-11-02 — NY STRUKTUR OCH
// FRÅGEMODELL FÖR F-01". Three parts, every question, option and patient-group
// rule verbatim from that section, which "ersätter äldre motstridiga
// frågelistor och ordningsbeslut i dokumentet".
//
// Versioning, per the same section: "Nya eller sammanslagna frågor får nya
// question_id. Ett tidigare question_id får bara återanvändas när frågans
// betydelse och svarsalternativ är oförändrade." So:
//   - kept keys (meaning and options unchanged, wording at most tightened):
//     weightKg, heightCm, prescriptionTransfer, reproductiveStatus,
//     type1Diabetes, mtcMen2, howLongTried, methodsTried, weightMedHistory,
//     impactDegree, desiredSupport(+Other), desiredOutcome(+Other). The
//     option VALUES are untouched, so every stop rule, the transfer routing
//     and the BMI bands read v13 exactly as they read v12.
//   - new keys: hormonalLifePhase (replaces lifeStage: single choice, other
//     options), weightChangeLifePhase (replaces changeContext: women-specific
//     phases only, optional) and weightDifficulties (merges bodySignals,
//     biggestObstacles and emotionalEating, max three).
//   - not asked any more: lifeStage, changeContext, bodySignals,
//     biggestObstacles, emotionalEating, howFoundUs(+Other). Stored answers
//     keep them; `templateVersion` says which instrument produced them.
//
// Patient-group rule: "För receptövertagspatient: Den generella frågan om
// tidigare försök visas inte." methodsTried (and so its follow-up) shows only
// for the four prescriptionTransfer answers that are not the active transfer
// choice, using the existing triggerValues mechanism every client already
// understands.
//
// The early safety questions keep their keys, values and stop rules
// ("Routing följer befintligt godkänt beslut"); only the doc's introduction
// line and MTC/MEN 2's "Varför frågar vi?" text are new.
// ---------------------------------------------------------------------------

export const F01_SECTION_ABOUT_YOU = "about_you";
export const F01_SECTION_WEIGHT_JOURNEY = "weight_journey";
export const F01_SECTION_SEEKING_NOW = "seeking_now";

export const HORMONAL_LIFE_PHASE_KEY = "hormonalLifePhase";
export const WEIGHT_CHANGE_LIFE_PHASE_KEY = "weightChangeLifePhase";
export const WEIGHT_DIFFICULTIES_KEY = "weightDifficulties";
export const WEIGHT_DIFFICULTIES_MAX_SELECTIONS = 3;

// Every prescriptionTransfer answer except the active transfer choice.
const NOT_TRANSFER_VALUES = (PRESCRIPTION_TRANSFER_QUESTION.options ?? [])
  .map((o) => o.value)
  .filter((v) => v !== PRESCRIPTION_TRANSFER_ACTIVE_CHOICE);

function v12Field(key: string): BmiQuizField {
  const field = bmiQuizV12.fields.find((f) => f.key === key);
  if (!field) throw new Error(`bmiQuizV12 has no ${key} field`);
  return field;
}

/** A v12 field with some option labels replaced, values untouched. */
function relabelled(
  field: BmiQuizField,
  labels: Readonly<Record<string, Localized>>,
): BmiQuizOption[] {
  return (field.options ?? []).map((o) =>
    labels[o.value] ? { ...o, label: labels[o.value]! } : o,
  );
}

export const bmiQuizV2: BmiQuizTemplate = {
  key: "bmi-quiz",
  version: 13,
  sections: [
    {
      key: F01_SECTION_ABOUT_YOU,
      title: { sv: "Om dig och din hälsa", en: "About you and your health" },
    },
    {
      key: F01_SECTION_WEIGHT_JOURNEY,
      title: { sv: "Din viktresa", en: "Your weight journey" },
    },
    {
      key: F01_SECTION_SEEKING_NOW,
      title: { sv: "Vad du söker nu", en: "What you are looking for now" },
    },
  ],
  fields: [
    // --- Part 1, "Om dig och din hälsa" ---------------------------------
    {
      ...v12Field(F01_WEIGHT_KEY),
      section: F01_SECTION_ABOUT_YOU,
      help: {
        sv: "Om du inte vet exakt kan du ange din bästa uppskattning.",
        en: "If you don't know exactly, you can give your best estimate.",
      },
    },
    {
      // The doc gives height no help text; v12's BMI sentence is not carried.
      key: "heightCm",
      kind: "numeric",
      unit: "cm",
      numeric: { min: 120, max: 230 },
      label: { sv: "Hur lång är du? (cm)", en: "How tall are you? (cm)" },
      required: true,
      section: F01_SECTION_ABOUT_YOU,
    },
    {
      ...PRESCRIPTION_TRANSFER_QUESTION,
      section: F01_SECTION_ABOUT_YOU,
      options: relabelled(PRESCRIPTION_TRANSFER_QUESTION, {
        uppehall: {
          sv: "Jag har nyligen gjort uppehåll eller avslutat behandlingen",
          en: "I have recently paused or ended the treatment",
        },
      }),
    },
    {
      ...v12Field(REPRODUCTIVE_STATUS_KEY),
      section: F01_SECTION_ABOUT_YOU,
      label: {
        sv: "Vad stämmer för dig när det gäller graviditet och amning?",
        en: "What is true for you when it comes to pregnancy and breastfeeding?",
      },
      options: relabelled(v12Field(REPRODUCTIVE_STATUS_KEY), {
        not_pregnant: {
          sv: "Jag är inte gravid, ammar inte och försöker inte bli gravid",
          en: "I am not pregnant, not breastfeeding and not trying to get pregnant",
        },
        pregnant: { sv: "Jag är gravid", en: "I am pregnant" },
        breastfeeding: { sv: "Jag ammar", en: "I am breastfeeding" },
        trying: {
          sv: "Jag försöker bli gravid",
          en: "I am trying to get pregnant",
        },
        planning: {
          sv: "Jag planerar att försöka bli gravid",
          en: "I am planning to try to get pregnant",
        },
        unsure: { sv: "Jag är osäker", en: "I am not sure" },
        prefer_not_to_say: {
          sv: "Jag vill inte ange",
          en: "I prefer not to say",
        },
      }),
    },
    {
      key: HORMONAL_LIFE_PHASE_KEY,
      kind: "single_select",
      section: F01_SECTION_ABOUT_YOU,
      label: {
        sv: "Var befinner du dig i din mens- eller hormonella livsfas?",
        en: "Where are you in your menstrual or hormonal life phase?",
      },
      help: {
        sv: "Kroppen och förutsättningarna kan förändras genom livet. Frågan hjälper oss att förstå om någon kvinnospecifik livsfas påverkar din viktresa.",
        en: "The body and its conditions can change through life. The question helps us understand whether a women-specific life phase affects your weight journey.",
      },
      required: false,
      optional: true,
      options: [
        {
          value: "regular_periods",
          label: {
            sv: "Jag har regelbunden mens",
            en: "I have regular periods",
          },
        },
        {
          value: "irregular_or_changed_periods",
          label: {
            sv: "Jag har oregelbunden eller förändrad mens",
            en: "I have irregular or changed periods",
          },
        },
        {
          value: "perimenopause",
          label: {
            sv: "Jag befinner mig i perimenopaus eller klimakteriet",
            en: "I am in perimenopause or menopause",
          },
        },
        {
          value: "after_menopause",
          label: {
            sv: "Jag har passerat klimakteriet",
            en: "I am past menopause",
          },
        },
        {
          value: "no_periods_other_reason",
          label: {
            sv: "Jag har inte mens av en annan anledning",
            en: "I don't have periods for another reason",
          },
        },
        {
          value: "none_of_these",
          label: {
            sv: "Inget av detta stämmer för mig",
            en: "None of this applies to me",
          },
        },
        {
          value: "unsure",
          label: { sv: "Jag är osäker", en: "I am not sure" },
        },
        {
          value: "prefer_not_to_say",
          label: { sv: "Jag vill inte ange", en: "I prefer not to say" },
        },
      ],
    },
    {
      ...TYPE_1_DIABETES_QUESTION,
      section: F01_SECTION_ABOUT_YOU,
      // "Frågorna visas tillsammans under introduktionen:"
      lead: {
        sv: "För att kunna visa rätt nästa steg behöver vi ställa två frågor om läkemedelsbehandlingens säkerhet.",
        en: "To show you the right next step, we need to ask two questions about the safety of the medication treatment.",
      },
    },
    {
      ...MTC_MEN2_QUESTION,
      section: F01_SECTION_ABOUT_YOU,
      label: {
        sv: "Har du eller någon av dina biologiska föräldrar, syskon eller barn fått diagnosen medullär sköldkörtelcancer, MTC, eller multipel endokrin neoplasi typ 2, MEN 2?",
        en: "Have you or any of your biological parents, siblings or children been diagnosed with medullary thyroid cancer, MTC, or multiple endocrine neoplasia type 2, MEN 2?",
      },
      why: {
        sv: "MTC och MEN 2 är ovanliga medicinska tillstånd som kan påverka om vissa läkemedel är lämpliga. Om du inte känner igen diagnoserna kan du välja ”Jag vet inte”.",
        en: "MTC and MEN 2 are rare medical conditions that can affect whether certain medicines are suitable. If you don't recognise the diagnoses, you can choose “I don't know”.",
      },
    },

    // --- Part 2, "Din viktresa" ------------------------------------------
    { ...v12Field("howLongTried"), section: F01_SECTION_WEIGHT_JOURNEY },
    {
      key: WEIGHT_CHANGE_LIFE_PHASE_KEY,
      kind: "multi_select",
      section: F01_SECTION_WEIGHT_JOURNEY,
      label: {
        sv: "Ser du ett samband mellan förändringen i din vikt och någon av följande faser i livet?",
        en: "Do you see a link between the change in your weight and any of the following phases of life?",
      },
      help: {
        sv: "Välj gärna flera alternativ.",
        en: "Feel free to pick several.",
      },
      required: false,
      optional: true,
      options: [
        {
          value: "pregnancy",
          label: {
            sv: "Under eller efter en graviditet",
            en: "During or after a pregnancy",
          },
        },
        {
          value: "periods_changed",
          label: {
            sv: "När mensen började förändras eller blev oregelbunden",
            en: "When my periods started to change or became irregular",
          },
        },
        {
          value: "perimenopause",
          label: {
            sv: "Under perimenopaus eller klimakteriet",
            en: "During perimenopause or menopause",
          },
        },
        {
          value: "after_menopause",
          label: { sv: "Efter klimakteriet", en: "After menopause" },
        },
        {
          // Ö-531's default for "don't know"-type options: exclusive.
          value: "no_clear_link",
          exclusive: true,
          label: {
            sv: "Jag ser inget tydligt samband med någon av dessa faser",
            en: "I see no clear link with any of these phases",
          },
        },
        {
          value: "dont_know",
          exclusive: true,
          label: { sv: "Jag vet inte", en: "I don't know" },
        },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      key: WEIGHT_DIFFICULTIES_KEY,
      kind: "multi_select",
      section: F01_SECTION_WEIGHT_JOURNEY,
      label: {
        sv: "Vad upplever du framför allt gör din vikt svår att påverka i dag?",
        en: "What do you feel mainly makes your weight hard to influence today?",
      },
      help: { sv: "Välj högst tre alternativ.", en: "Pick at most three." },
      required: true,
      maxSelections: WEIGHT_DIFFICULTIES_MAX_SELECTIONS,
      options: [
        {
          value: "hunger_fullness",
          label: {
            sv: "Hunger eller svårt att känna mig mätt",
            en: "Hunger or finding it hard to feel full",
          },
        },
        {
          value: "cravings_food_thoughts",
          label: {
            sv: "Sug eller mycket tankar på mat",
            en: "Cravings or thinking about food a lot",
          },
        },
        {
          value: "stress_emotions_pause",
          label: {
            sv: "Stress, känslor eller att mat blir ett sätt att få paus",
            en: "Stress, emotions or food becoming a way to get a break",
          },
        },
        {
          value: "time_routines",
          label: {
            sv: "Brist på tid eller rutiner som fungerar i vardagen",
            en: "Lack of time or of routines that work in everyday life",
          },
        },
        {
          value: "fatigue_low_energy",
          label: {
            sv: "Trötthet eller låg energi",
            en: "Tiredness or low energy",
          },
        },
        {
          value: "changes_dont_last",
          label: {
            sv: "Jag får till förändringar, men har svårt att få dem att hålla",
            en: "I manage to make changes, but find it hard to make them last",
          },
        },
        {
          value: "regain",
          label: {
            sv: "Jag går ner i vikt men går ofta upp igen",
            en: "I lose weight but often gain it back",
          },
        },
        {
          value: "dont_know",
          exclusive: true,
          label: { sv: "Jag vet inte riktigt", en: "I don't really know" },
        },
        { value: "other", label: { sv: "Annat", en: "Other" } },
      ],
    },
    {
      ...v12Field("methodsTried"),
      section: F01_SECTION_WEIGHT_JOURNEY,
      label: {
        sv: "Vad har du tidigare provat för att påverka din vikt?",
        en: "What have you tried before to influence your weight?",
      },
      // The doc's "Typ:" line, shown as the help line the way v12 showed its
      // caps; the same for weightChangeLifePhase and weightDifficulties.
      help: { sv: "Välj allt som stämmer.", en: "Select all that apply." },
      required: false,
      optional: true,
      showWhen: {
        parentKey: PRESCRIPTION_TRANSFER_KEY_F01,
        triggerValues: NOT_TRANSFER_VALUES,
      },
    },
    {
      ...v12Field("weightMedHistory"),
      section: F01_SECTION_WEIGHT_JOURNEY,
      options: relabelled(v12Field("weightMedHistory"), {
        using_today: {
          sv: "Jag använder behandlingen i dag",
          en: "I use the treatment today",
        },
        past_good_effect: {
          sv: "Jag använde den tidigare med god effekt",
          en: "I used it before with good effect",
        },
        stopped_other_reason: {
          sv: "Jag slutade av en annan anledning",
          en: "I stopped for another reason",
        },
      }),
    },

    // --- Part 3, "Vad du söker nu" ---------------------------------------
    {
      ...v12Field("impactDegree"),
      section: F01_SECTION_SEEKING_NOW,
      label: {
        sv: "Hur mycket påverkar vikten din hälsa eller vardag i dag?",
        en: "How much does weight affect your health or everyday life today?",
      },
    },
    { ...v12Field(DESIRED_SUPPORT_KEY), section: F01_SECTION_SEEKING_NOW },
    {
      ...v12Field(DESIRED_SUPPORT_OTHER_KEY),
      section: F01_SECTION_SEEKING_NOW,
    },
    {
      ...v12Field(DESIRED_OUTCOME_KEY),
      section: F01_SECTION_SEEKING_NOW,
      options: relabelled(v12Field(DESIRED_OUTCOME_KEY), {
        other: { sv: "Annat", en: "Other" },
      }),
    },
    { ...v12Field("desiredOutcomeOther"), section: F01_SECTION_SEEKING_NOW },
  ],
};

// ---------------------------------------------------------------------------
// TES-780 — the quiz as a pre-v9 client sees it.
//
// v9 (TES-733, #712) re-introduced `prescriptionTransfer` as a required,
// always-visible field. The app does not fetch the quiz — it bundles it — so
// every build cut before v9 submits an answer set without that key, and the
// server's required-answer check rejects it. F-01 is the first step of the
// journey, so that is a closed door rather than a degraded screen.
//
// This is v9's field list minus exactly that one field, which is byte-for-byte
// v8's list: v8 → v9 added the question and changed nothing else (verified by
// comparing the two field sets). So the `version: 8` stamp below is the truth
// about what such a client answered, not a convenient lie — and that matters,
// because `templateVersion` is how a stored answer set says which instrument
// produced it. The same file's history shows what happens when that slips:
// v3–v7's `ja`/`osaker` were answers to a DIFFERENT question than v9's
// `ja_fortsatt`, and untangling that afterwards took Ö-154 and Ö-158.
//
// An absent answer routes to NEW_TREATMENT (`derivePatientGroup` →
// `isPrescriptionTransfer` returns false for a missing key), which is exactly
// what a v8 client meant when it could not ask. Nothing is invented on the
// patient's behalf: not asked stays not answered.
//
// DELETE THIS, and its gate in f01.service.ts, once no build older than
// CLIENT_CONTRACT_CHANGES.f01TransferQuestion is in use. It has exactly one
// consumer so that removal is a two-file change.
export const F01_VERSION_BEFORE_TRANSFER_QUESTION = 8;

// ---------------------------------------------------------------------------
// TES-1164 — the quiz as an app build before the safety questions sees it.
//
// The app fetches the questions from `/f01/quiz` (TES-803), so app
// 2.0.4–2.0.6 WOULD render the two new single_selects. What they cannot
// render is the result: those builds draw every stop with their own
// "Det här svaret behöver bedömas innan behandling" page and a "Boka
// vårdmöte" button, and §13/§14 offer no meeting at all ("Ella kan inte
// erbjuda behandling i vårt digitala vårdflöde", CTA "Avsluta"). So those
// builds are served v9 — v10 minus exactly these two fields — and their
// answers are stamped v9, which is the truth about what they answered. A
// missing answer is "not asked": it stops nothing and flags nothing, and
// F-02 v17 asks the questions those patients never got (intake-content.v17).
//
// DELETE THIS, with its gate in f01.service.ts and the F-02 fallback, once no
// build older than CLIENT_CONTRACT_CHANGES.f01SafetyQuestions is in use.
export const F01_VERSION_BEFORE_SAFETY_QUESTIONS = 9;

const SAFETY_QUESTION_KEYS: ReadonlySet<string> = new Set([
  TYPE_1_DIABETES_KEY,
  MTC_MEN2_KEY,
]);

// ---------------------------------------------------------------------------
// TES-1319 — the quiz as it was before SUIT_DESIRED_SUPPORT (v10).
//
// v11 is server-only: the app fetches the quiz, the web ships with the API. So
// at deploy, a form someone opened against v10 (a web tab loaded before the
// deploy) is still being answered and would be refused for a question it never
// showed. A submission that says it was given against v10 is validated against
// exactly v10's field set (v11 minus the two new keys) and stamped 10 — every
// question v10 required is still required, so the claim cannot skip anything
// that existed in that version.
//
// DELETE THIS (and its uses in leads.controller.ts and f01.service.ts) once no
// v10 form can still be open, i.e. a few days after v11 is deployed.
export const F01_VERSION_BEFORE_DESIRED_SUPPORT = 10;

export const bmiQuizWithoutDesiredSupport: BmiQuizTemplate = {
  ...bmiQuizV11,
  version: F01_VERSION_BEFORE_DESIRED_SUPPORT,
  fields: bmiQuizV11.fields.filter(
    (field) => !DESIRED_SUPPORT_KEYS.has(field.key),
  ),
};

/**
 * The instrument a submission to the CURRENT quiz is validated against: v10
 * when it says it was answered against v10, v11 when it says v11 (TES-1533:
 * a web tab opened before v12's deploy showed v11's weight question, and
 * the stored row says so), v12 when it says v12 (TES-1658: a form opened
 * before v13's deploy asked v12's questions), otherwise `current` unchanged.
 * Any other claimed version keeps today's behaviour.
 *
 * DELETE the v12 arm a few days after v13 is deployed, once no v12 form can
 * still be open.
 */
export function bmiQuizForSubmittedVersion(
  current: BmiQuizTemplate,
  templateVersion: number | undefined,
): BmiQuizTemplate {
  if (current !== bmiQuizV2) return current;
  if (templateVersion === F01_VERSION_BEFORE_DESIRED_SUPPORT) {
    return bmiQuizWithoutDesiredSupport;
  }
  if (templateVersion === bmiQuizV12.version) return bmiQuizV12;
  return templateVersion === bmiQuizV11.version ? bmiQuizV11 : current;
}

/**
 * TES-1658 — the part a field is presented in: its 1-based position among
 * the template's sections, how many there are, and its title. Null when the
 * template has no sections (v12 and older) or the field names none.
 */
export function bmiQuizSectionOf(
  template: Pick<BmiQuizTemplate, "sections">,
  field: Pick<BmiQuizField, "section">,
): { index: number; total: number; title: Localized } | null {
  const sections = template.sections ?? [];
  const at = sections.findIndex((s) => s.key === field.section);
  if (at === -1) return null;
  return { index: at + 1, total: sections.length, title: sections[at]!.title };
}

// Both older instruments below were derived from bmiQuizV2's fields; they now
// derive from v10's so v11's question (and v12's wording, TES-1533) cannot
// leak into them while they still stamp 9 and 8.
export const bmiQuizWithoutSafetyQuestions: BmiQuizTemplate = {
  ...bmiQuizV2,
  version: F01_VERSION_BEFORE_SAFETY_QUESTIONS,
  fields: bmiQuizWithoutDesiredSupport.fields.filter(
    (field) => !SAFETY_QUESTION_KEYS.has(field.key),
  ),
};

export const bmiQuizWithoutTransferQuestion: BmiQuizTemplate = {
  ...bmiQuizV2,
  version: F01_VERSION_BEFORE_TRANSFER_QUESTION,
  fields: bmiQuizWithoutSafetyQuestions.fields.filter(
    (field) => field.key !== PRESCRIPTION_TRANSFER_KEY_F01,
  ),
};
